import { QueryClient, useQuery } from "@tanstack/react-query";
import { getArtifact, listArtifactsForEntity } from "@/services/DataService";
import type { AttachedArtifact, TaskArtifacts } from "@/types/artifact";
import { CARD_LINKS_KEY } from "./useCardLinks";

/**
 * One artifact's details and the presigned URLs its page is served from.
 *
 * Read by id rather than taken from the card that opened it, so an artifact
 * the agent has rewritten since shows its current version and the URLs are
 * freshly signed. Pass the version a card announced to keep opening that
 * version. Refetched after a tool turn by `invalidateArtifactQueries`.
 *
 * Nothing is kept fresh in the background: the caller enables this while its
 * panel is open, and every open reads again. The read exists to mint the URLs
 * the page is served from, and those expire within the hour, so reusing an
 * older read is how a panel ends up pointing at a signature that has run out.
 */
export const useArtifact = (
  artifactId: string,
  version?: number | null,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ["artifact", artifactId, version ?? "current"],
    queryFn: () => getArtifact(artifactId, version ?? undefined),
    enabled: (options?.enabled ?? true) && Boolean(artifactId),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const TASK_ARTIFACTS_KEY = ["task-artifacts"] as const;

export const taskArtifactsKey = (taskId: string) => ["task-artifacts", taskId] as const;

/**
 * Split a task's attachments into the two slots the panel shows. A relation
 * type this client does not know is left out rather than guessed at.
 */
const groupBySlot = (attached: AttachedArtifact[]): TaskArtifacts => {
  return {
    notes: attached.filter((artifact) => artifact.relation_type === "task_notes"),
    outputs: attached.filter((artifact) => artifact.relation_type === "task_outputs"),
  };
};

/**
 * What a task holds, split into its notes and its outputs.
 *
 * Read separately from the task itself rather than folded into it, so the task
 * cache is untouched by an attachment and the task service stays free of
 * artifacts. Read while the panel is open, for the same reason as one
 * artifact: the URLs it carries are short-lived, and the attachment list is
 * whatever the chat has done to the task since.
 */
export const useTaskArtifacts = (
  taskId: string | null,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: taskArtifactsKey(taskId ?? ""),
    queryFn: () => listArtifactsForEntity("task", taskId ?? ""),
    enabled: Boolean(taskId) && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: groupBySlot,
  });
};

/** The chat tools that can change an artifact behind the canvas's back. */
const ARTIFACT_WRITING_TOOLS = [
  "create_artifact",
  "update_artifact",
  "delete_artifact",
  "task_attach_artifact",
  "task_detach_artifact",
  "card_attach_artifact",
  "card_detach_artifact",
];

/**
 * Refreshes an open artifact canvas after the agent has written to it, so a
 * page rewritten by asking in chat updates in place, along with the task
 * attachment and card link lists that show it.
 *
 * Gated on the tools that actually write: invalidating after every tool round
 * would refetch an artifact because an unrelated chart was drawn.
 */
export const invalidateArtifactQueries = (
  queryClient: QueryClient,
  executedToolNames: string[],
) => {
  if (!executedToolNames.some((name) => ARTIFACT_WRITING_TOOLS.includes(name))) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: ["artifact"] });
  queryClient.invalidateQueries({ queryKey: TASK_ARTIFACTS_KEY });
  queryClient.invalidateQueries({ queryKey: CARD_LINKS_KEY });
};
